// Angular
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

/**
 * Raw `window.location.hash` captured in `main.ts` at startup — before any
 * route guard / bootstrap redirect can strip it. Mirrors the legacy web
 * router, which read `window.location.hash` directly.
 */
export const INITIAL_LOCATION_HASH = new InjectionToken<string>('INITIAL_LOCATION_HASH');

/** Per-leg map of runnerId -> selection value (mirrors HelpService). */
export type EncodedRunnerLeg = Record<string, string>;

/** Parsed wager deep-link (port of the legacy web `#/wager` route). */
export interface IWagerDeepLink {
  eventId: string;
  raceId: string;
  poolId: string;
  amount: string;
  /** Full-word modifier, e.g. 'PowerBox', 'Box', 'Straight'. */
  modifier: string;
  /** Raw runner token as it appears in the URL. */
  encodedRunners: string;
  /** Decoded per-leg runner selections. */
  selectionRunners: EncodedRunnerLeg[];
}

/**
 * Parses the shareable wager deep-link the Help panel generates
 * (`#/wager/eventId/raceId/poolId/amount/modifier/encodedRunners`)
 * so the wager page can replay the selection — mirrors the legacy
 * web router without the DOM/globals it used to read.
 */
@Injectable({ providedIn: 'root' })
export class WagerDeepLinkService {
  /** Startup hash (e.g. '#/wager/…'), captured before any redirect. */
  private readonly startupHash: string;

  /** True once the startup deep-link has been applied (blocks re-apply). */
  private consumed = false;

  constructor(
    @Optional() @Inject(INITIAL_LOCATION_HASH) initialHash: string | null
  ) {
    this.startupHash =
      initialHash ?? (typeof window !== 'undefined' ? window.location.hash : '');
  }

  /**
   * Raw captured startup hash, used to restore the shareable URL.
   * @returns {string} The hash captured at app start (e.g. '#/wager/…')
   */
  get rawHash(): string {
    return this.startupHash;
  }

  /**
   * Whether the startup deep-link has already been applied.
   * @returns {boolean} True once markConsumed() ran
   */
  get isConsumed(): boolean {
    return this.consumed;
  }

  /**
   * Parse the captured startup hash without consuming it.
   * @returns {IWagerDeepLink | null} Parsed link, or null when not a wager link
   */
  peekInitialDeepLink(): IWagerDeepLink | null {
    const link = this.parseFromHash(this.startupHash);
    // TEMP diagnostics — remove once the deep-link flow is verified.
    console.warn('[deep-link] startupHash =', JSON.stringify(this.startupHash), '=> parsed =', link);
    return link;
  }

  /**
   * Mark the startup deep-link as applied so later instances skip it.
   * @returns {void}
   */
  markConsumed(): void {
    this.consumed = true;
  }

  /**
   * Parse a wager deep-link into its parts (trailing parts optional).
   * Accepts a router fragment (`/wager/…`) or a raw location hash (`#/wager/…`).
   * @param {string} fragment Route fragment or location hash
   * @returns {IWagerDeepLink | null} Parsed link, or null when not a wager link
   */
  parseFromHash(fragment: string = window.location.hash): IWagerDeepLink | null {
    if (!fragment) return null;

    const parts = fragment.replace(/^#/, '').replace(/^\//, '').split('/');
    const [route, eventId, raceId, poolId, amount, modifier, encodedRunners] = parts;
    if (route !== 'wager' || !eventId) return null;

    return {
      eventId,
      raceId: raceId ?? '',
      poolId: poolId ?? '',
      amount: amount ?? '',
      modifier: modifier ?? '',
      encodedRunners: encodedRunners ?? '',
      selectionRunners: this.decodeRunners(encodedRunners ?? '')
    };
  }

  /**
   * Decode a runner token back into per-leg selections
   * @param {string} encoded Encoded token from the URL
   * @returns {EncodedRunnerLeg[]} Per-leg runner maps (empty on failure)
   */
  decodeRunners(encoded: string): EncodedRunnerLeg[] {
    if (!encoded) return [];
    try {
      return JSON.parse(atob(decodeURIComponent(encoded)));
    } catch {
      return [];
    }
  }

  /**
   * Map a full-word modifier to its runner-grid modifier chip selector
   * @param {string} modifier Full-word modifier (e.g. 'PowerBox')
   * @returns {string} `data-cy` selector for the matching modifier chip
   */
  modifierChipSelector(modifier: string): string {
    switch (modifier) {
      case 'Box': return '[data-cy="modifier-box"]';
      case 'KeyBox': return '[data-cy="modifier-keybox"]';
      case 'PowerBox': return '[data-cy="modifier-powerbox"]';
      case 'KeyWheel': return '[data-cy="modifier-keywheel"]';
      case 'DutchCost': return '[data-cy="modifier-dutch"]';
      case 'ABC': return '[data-cy="modifier-abc"]';
      default: return '[data-cy="modifier-straight"]';
    }
  }
}
